import React from 'react'
import Plot from 'react-plotly.js'
import { Paper, Typography } from '@mui/material'

interface DTCTimelineChartProps {
    title: string
}

export const DTCTimelineChart: React.FC<DTCTimelineChartProps> = ({
    title,
}) => {
    // Generate sample timeline data
    const generateTimelineData = () => {
        const dates: string[] = []
        const uniqueEvents: number[] = []
        const uniqueCodes: number[] = []
        const activeBoats: number[] = []

        const startDate = new Date('2024-09-01')
        const endDate = new Date('2025-04-30')

        for (
            let d = new Date(startDate);
            d <= endDate;
            d.setDate(d.getDate() + 1)
        ) {
            dates.push(d.toISOString().split('T')[0])

            // Generate realistic DTC patterns with some spikes
            const baseUnique =
                50 +
                Math.sin(
                    (d.getTime() - startDate.getTime()) /
                        (1000 * 60 * 60 * 24 * 30)
                ) *
                    20
            const baseCodes =
                40 +
                Math.sin(
                    (d.getTime() - startDate.getTime()) /
                        (1000 * 60 * 60 * 24 * 20)
                ) *
                    15
            const baseBoats =
                30 +
                Math.sin(
                    (d.getTime() - startDate.getTime()) /
                        (1000 * 60 * 60 * 24 * 15)
                ) *
                    10

            // Add some random spikes
            const spike = Math.random() > 0.95 ? Math.random() * 100 : 0

            uniqueEvents.push(
                Math.max(0, baseUnique + (Math.random() - 0.5) * 30 + spike)
            )
            uniqueCodes.push(
                Math.max(
                    0,
                    baseCodes + (Math.random() - 0.5) * 20 + spike * 0.8
                )
            )
            activeBoats.push(
                Math.max(
                    0,
                    baseBoats + (Math.random() - 0.5) * 15 + spike * 0.6
                )
            )
        }

        return { dates, uniqueEvents, uniqueCodes, activeBoats }
    }

    const { dates, uniqueEvents, uniqueCodes, activeBoats } =
        generateTimelineData()

    return (
        <Paper sx={{ p: 2, height: 350 }}>
            <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
                {title}
            </Typography>
            <Plot
                data={[
                    {
                        x: dates,
                        y: uniqueEvents,
                        type: 'scatter',
                        mode: 'lines',
                        name: 'Unique Events',
                        line: { color: '#4CAF50', width: 2 },
                        fill: 'tonexty',
                        fillcolor: 'rgba(76, 175, 80, 0.1)',
                    },
                    {
                        x: dates,
                        y: uniqueCodes,
                        type: 'scatter',
                        mode: 'lines',
                        name: 'Unique Codes',
                        line: { color: '#FFA726', width: 2 },
                        fill: 'tonexty',
                        fillcolor: 'rgba(255, 167, 38, 0.1)',
                    },
                    {
                        x: dates,
                        y: activeBoats,
                        type: 'scatter',
                        mode: 'lines',
                        name: 'Active Boats',
                        line: { color: '#2196F3', width: 2 },
                        fill: 'tozeroy',
                        fillcolor: 'rgba(33, 150, 243, 0.1)',
                    },
                ]}
                layout={{
                    height: 280,
                    margin: { t: 10, r: 30, l: 50, b: 50 },
                    paper_bgcolor: '#2d2d2d',
                    plot_bgcolor: '#2d2d2d',
                    font: {
                        color: 'white',
                        family: 'Roboto, sans-serif',
                        size: 10,
                    },
                    xaxis: {
                        gridcolor: '#444444',
                        showgrid: true,
                        zeroline: false,
                        tickfont: { color: 'white', size: 9 },
                        tickangle: -45,
                        dtick: 'M1',
                        tickformat: '%b %Y',
                    },
                    yaxis: {
                        gridcolor: '#444444',
                        showgrid: true,
                        zeroline: false,
                        tickfont: { color: 'white', size: 9 },
                        range: [0, 200],
                    },
                    yaxis2: {
                        overlaying: 'y',
                        side: 'right',
                        gridcolor: 'transparent',
                        showgrid: false,
                        zeroline: false,
                        tickfont: { color: 'white', size: 9 },
                        range: [0, 400],
                    },
                    showlegend: true,
                    legend: {
                        orientation: 'h',
                        y: -0.2,
                        x: 0,
                        font: { size: 9, color: 'white' },
                    },
                    hovermode: 'x unified',
                }}
                config={{
                    displayModeBar: false,
                    responsive: true,
                }}
                style={{ width: '100%', height: '100%' }}
            />
        </Paper>
    )
}
